'use client';

import { useRef, ReactNode } from 'react';
import { motion, useInView } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';

const EASE = [0.22, 1, 0.36, 1] as const;

// ── Primitives ──────────────────────────────────────────────────────────────

export function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ num, label }: { num: string; label: string }) {
  return (
    <div
      className="flex items-center gap-2.5 font-mono text-[12px] font-medium tracking-[0.14em] uppercase"
      style={{ color: 'var(--accent)' }}
    >
      <span>{num} /</span>
      {label}
      <span className="flex-1 max-w-[64px] h-px bg-stone-300 dark:bg-zinc-700" />
    </div>
  );
}

export function HR() {
  return <div className="h-px bg-stone-200 dark:bg-zinc-800 mx-4 sm:mx-12" />;
}

const PHOTOS = 'https://hatmwfdriyykbwcnepwr.supabase.co/storage/v1/object/public/slab-photos';

// ── Shot mock bar ────────────────────────────────────────────────────────────

function ShotMockBar({ label }: { label: string }) {
  return (
    <div className="h-8 border-b border-stone-200 dark:border-zinc-800 flex items-center px-3 gap-2 shrink-0">
      {[0, 1, 2].map(i => (
        <span key={i} className="w-2.5 h-2.5 rounded-full bg-stone-300 dark:bg-zinc-700" />
      ))}
      <span className="font-mono text-[11px] text-stone-400 dark:text-zinc-500 ml-3">{label}</span>
    </div>
  );
}

// ── Shot mocks ───────────────────────────────────────────────────────────────

export function ShotStockTable() {
  const tS = useTranslations('Shots');
  const rows = [
    { name: 'Carrara Bianco · C2', dim: '320×160', qty: '12.48', st: 'success', photo: 'bianco-carrara-c.jpg' },
    { name: 'Nero Marquina · M4',  dim: '280×140', qty:  '3.92', st: 'warning', photo: 'nero-assoluto-zimbabwe.jpg' },
    { name: 'Calacatta Oro',        dim: '310×155', qty:  '0.00', st: 'error',   photo: 'calacatta-oro.jpg' },
    { name: 'Statuario Venato',     dim: '300×160', qty:  '8.20', st: 'success', photo: 'statuario-venato.jpg' },
    { name: 'Travertino Romano',    dim: '320×155', qty:  '5.40', st: 'success', photo: 'travertino-romano-classico.jpg' },
    { name: 'Grigio Carnico',       dim: '270×140', qty:  '9.60', st: 'success', photo: 'grigio-carnico.jpg' },
  ] as const;

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label={`lithosbase.app / ${tS('urlStock')}`} />
      <div className="flex-1 px-4 pt-3 pb-2 flex flex-col" style={{ overflowY: 'auto', scrollbarWidth: 'none' }}>
        <div className="flex items-center justify-end mb-2">
          <span className="text-[11px] text-stone-400 dark:text-zinc-500 font-mono">48 righe</span>
        </div>
        {rows.map((r) => (
          <div
            key={r.name}
            className="grid items-center py-[6px] border-t border-stone-100 dark:border-zinc-800"
            style={{ gridTemplateColumns: '28px 1fr 52px 16px' }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`${PHOTOS}/${r.photo}`}
              alt=""
              className="w-6 h-6 rounded-[5px] shrink-0 object-cover"
            />
            <div className="px-2 min-w-0">
              <div className="text-[12px] font-medium text-stone-950 dark:text-zinc-50 truncate">{r.name}</div>
              <div className="font-mono text-[10px] text-stone-400 dark:text-zinc-500">{r.dim}</div>
            </div>
            <span className="font-mono text-[11px] text-stone-950 dark:text-zinc-50 text-right pr-2">
              {r.qty} <span className="text-stone-400 dark:text-zinc-500">mq</span>
            </span>
            <span
              className="w-2 h-2 rounded-full justify-self-end"
              style={{
                background:
                  r.st === 'success' ? 'var(--success)' :
                  r.st === 'warning' ? 'var(--warning)' :
                  'var(--danger)',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShotCatalog() {
  const tS = useTranslations('Shots');
  const items = [
    { name: 'Carrara Bianco', dim: '320×160', photo: 'bianco-carrara-c.jpg' },
    { name: 'Nero Marquina',  dim: '280×140', photo: 'nero-assoluto-zimbabwe.jpg' },
    { name: 'Calacatta Oro',  dim: '310×155', photo: 'calacatta-oro.jpg' },
    { name: 'Statuario',      dim: '300×160', photo: 'statuario-venato.jpg' },
    { name: 'Travertino',     dim: '320×155', photo: 'travertino-romano-classico.jpg' },
    { name: 'Grigio Carnico', dim: '270×140', photo: 'grigio-carnico.jpg' },
    { name: 'Quarella Ombria',dim: '290×150', photo: 'quarella-ombria.jpg' },
    { name: 'Bianco Perla',   dim: '310×155', photo: 'bianco-perla.jpg' },
    { name: 'Silestone Cal.', dim: '330×160', photo: 'silestone-eternal-calacatta.jpg' },
  ] as const;

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label="lithosbase.app / c / laboratorio-rossi" />
      <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
        <div className="flex gap-1.5 items-center flex-wrap">
          {[tS('filterAll'), tS('catMarble'), tS('catGranite'), tS('catQuartz'), tS('catCeramic')].map((c, i) => (
            <span
              key={c}
              className={`px-2 py-0.5 rounded-full text-[10px] border ${
                i === 0
                  ? 'bg-stone-950 dark:bg-zinc-50 text-stone-50 dark:text-zinc-950 border-transparent'
                  : 'text-stone-400 dark:text-zinc-500 border-stone-200 dark:border-zinc-700'
              }`}
            >
              {c}
            </span>
          ))}
        </div>
        <div
          className="grid grid-cols-3 gap-2"
          style={{ overflowY: 'auto', scrollbarWidth: 'none' }}
        >
          {items.map((item) => (
            <div
              key={item.name}
              className="flex flex-col rounded-[8px] overflow-hidden border border-stone-200 dark:border-zinc-800"
            >
              <div className="relative" style={{ aspectRatio: '4/3' }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={`${PHOTOS}/${item.photo}`}
                  alt={item.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="px-2 py-1.5 bg-white dark:bg-zinc-950">
                <div className="text-[10px] font-semibold text-stone-950 dark:text-zinc-50 truncate">{item.name}</div>
                <div className="font-mono text-[9px] text-stone-400 dark:text-zinc-500">{item.dim}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ShotTeam() {
  const tS = useTranslations('Shots');
  const members = [
    ['AR', 'Andrea Rossi',  'andrea@laboratorio-rossi.it',  'Owner'],
    ['MC', 'Marco Conti',   'marco@laboratorio-rossi.it',   'Admin'],
    ['EF', 'Elena Ferrari', 'elena@laboratorio-rossi.it',   'Operatore'],
    ['GP', 'Giulio Pini',   'giulio@laboratorio-rossi.it',  'Operatore'],
  ] as const;

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label={`lithosbase.app / admin / ${tS('urlTeam')}`} />
      <div className="flex-1 p-4 flex flex-col overflow-hidden">
        {members.map(([init, name, email, role]) => (
          <div key={email} className="flex items-center gap-2.5 py-2 border-t border-stone-100 dark:border-zinc-800">
            <div className="w-7 h-7 rounded-full bg-stone-100 dark:bg-zinc-800 border border-stone-200 dark:border-zinc-700 flex items-center justify-center font-mono text-[10.5px] font-semibold text-stone-400 dark:text-zinc-500 shrink-0">
              {init}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-stone-950 dark:text-zinc-50">{name}</div>
              <div className="text-[10.5px] text-stone-400 dark:text-zinc-500 truncate">{email}</div>
            </div>
            <span
              className={`px-2 py-0.5 rounded-[6px] text-[10px] tracking-[0.06em] uppercase font-medium ${
                role === 'Owner'
                  ? 'bg-stone-950 dark:bg-zinc-50 text-stone-50 dark:text-zinc-950'
                  : role === 'Admin'
                  ? 'bg-stone-500 text-white'
                  : 'bg-stone-100 dark:bg-zinc-800 text-stone-400 dark:text-zinc-500'
              }`}
            >
              {role}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShotQR() {
  const tS = useTranslations('Shots');
  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label={`lithosbase.app / ${tS('urlItems')} / LB-2410-A38`} />
      <div className="flex-1 p-6 flex flex-col items-center justify-center gap-4">
        <div
          className="w-40 h-40 border-8 border-white dark:border-zinc-950 outline outline-1 outline-stone-200 dark:outline-zinc-800"
          style={{
            backgroundImage:
              'linear-gradient(45deg, #0C0A09 25%, transparent 25%, transparent 75%, #0C0A09 75%), linear-gradient(45deg, #0C0A09 25%, transparent 25%, transparent 75%, #0C0A09 75%)',
            backgroundPosition: '0 0, 8px 8px',
            backgroundSize: '16px 16px',
          }}
        />
        <div className="text-center">
          <div className="font-mono text-[13px] font-semibold text-stone-950 dark:text-zinc-50">LB-2410-A38</div>
          <div className="text-[11.5px] text-stone-400 dark:text-zinc-500 mt-0.5">Carrara Bianco · C2 · 320×160</div>
        </div>
      </div>
    </div>
  );
}

export function ShotHistory() {
  const tS = useTranslations('Shots');
  const rows = [
    ['14 mag', 'CARICO',  'Carrara Bianco', '+8 lastre',  'Andrea'],
    ['14 mag', 'SCARICO', 'Nero Marquina',  '−2 lastre',  'Marco'],
    ['13 mag', 'TAGLIO',  'Calacatta Oro',  '→ 3 pezzi',  'Elena'],
    ['13 mag', 'CARICO',  'Verde Alpi',     '+12 lastre', 'Andrea'],
    ['12 mag', 'SCARICO', 'Travertino',     '−5 lastre',  'Giulio'],
  ] as const;

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label={`lithosbase.app / ${tS('urlMovements')}`} />
      <div className="flex-1 p-4 flex flex-col overflow-hidden">
        {rows.map(([date, type, name, qty, op], i) => (
          <div
            key={i}
            className="grid items-center py-[7px] border-t border-stone-100 dark:border-zinc-800 text-[11.5px]"
            style={{ gridTemplateColumns: '50px 80px 1fr 90px 70px' }}
          >
            <span className="font-mono text-stone-400 dark:text-zinc-500">{date}</span>
            <span
              className="inline-block px-1.5 py-0.5 rounded text-[9.5px] font-semibold tracking-[0.06em] uppercase w-fit"
              style={{
                background:
                  type === 'CARICO'  ? 'var(--success-subtle)' :
                  type === 'SCARICO' ? 'var(--danger-subtle)'  :
                  'var(--accent-soft)',
                color:
                  type === 'CARICO'  ? 'var(--success)' :
                  type === 'SCARICO' ? 'var(--danger)'  :
                  'var(--accent-strong)',
              }}
            >
              {type}
            </span>
            <span className="text-stone-950 dark:text-zinc-50 pl-1">{name}</span>
            <span className="font-mono text-stone-950 dark:text-zinc-50 text-right">{qty}</span>
            <span className="text-stone-400 dark:text-zinc-500 text-[10.5px] text-right">{op}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ShotSettings() {
  const tS = useTranslations('Shots');
  const cats = [
    { init: 'M', name: tS('catMarbleOne'),  prefix: 'MRM', c1: '#F0EDE6', c2: '#C9BFB0' },
    { init: 'G', name: tS('catGraniteOne'), prefix: 'GRN', c1: '#3d2b1f', c2: '#6b4c3b' },
    { init: 'Q', name: tS('catQuartzOne'),  prefix: 'QRZ', c1: '#c8c3b8', c2: '#9e9891' },
    { init: 'C', name: tS('catCeramicOne'), prefix: 'CER', c1: '#8a8783', c2: '#bfbcb6' },
  ] as const;

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-zinc-950">
      <ShotMockBar label={`lithosbase.app / ${tS('urlSettings')}`} />
      <div className="flex-1 p-4 flex flex-col gap-4 overflow-hidden">
        {/* Workspace card */}
        <div className="flex items-center gap-3 p-3 rounded-[10px] bg-stone-50 dark:bg-zinc-900 border border-stone-200 dark:border-zinc-800">
          <div className="w-9 h-9 rounded-[8px] bg-stone-950 dark:bg-zinc-50 flex items-center justify-center text-stone-50 dark:text-zinc-950 font-bold text-sm shrink-0">
            W
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-stone-950 dark:text-zinc-50 truncate">Workshop Lapideo</div>
            <div className="font-mono text-[10px] text-stone-400 dark:text-zinc-500 mt-0.5 truncate">{tS('units')}</div>
          </div>
        </div>

        {/* Categories */}
        <div>
          <div className="text-[9.5px] font-semibold tracking-[0.1em] uppercase text-stone-400 dark:text-zinc-500 mb-2">
            {tS('categories')}
          </div>
          {cats.map(({ init, name, prefix, c1, c2 }) => (
            <div key={name} className="flex items-center gap-3 py-2 border-t border-stone-100 dark:border-zinc-800">
              <div
                className="w-7 h-7 rounded-[6px] flex items-center justify-center font-bold text-[11px] shrink-0"
                style={{ background: `linear-gradient(140deg, ${c1}, ${c2})`, color: 'rgba(255,255,255,0.75)' }}
              >
                {init}
              </div>
              <div className="flex-1 text-[12px] font-semibold text-stone-950 dark:text-zinc-50">{name}</div>
              <span className="font-mono text-[10px] text-stone-400 dark:text-zinc-500 shrink-0">{prefix}</span>
              <span className="w-5 h-5 rounded-[5px] bg-stone-100 dark:bg-zinc-800 flex items-center justify-center shrink-0">
                <span className="block w-2.5 h-0.5 bg-stone-400 dark:bg-zinc-500 rounded-full" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────

export function MarketingFooter() {
  const t = useTranslations('Footer');

  const cols = [
    {
      label: t('product'),
      links: [
        { href: '/features',   label: t('features') },
        { href: '/pricing',    label: t('pricing') },
        { href: '/c/workshop', label: t('demo') },
      ],
    },
    {
      label: t('legal'),
      links: [
        { href: '/privacy', label: t('privacy') },
        { href: '/terms',   label: t('terms') },
        { href: '/contact', label: t('contact') },
      ],
    },
  ];

  return (
    <footer className="border-t border-stone-200 dark:border-zinc-800 py-12">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 flex items-start justify-between gap-8 flex-wrap">
        <div>
          <div className="flex items-center gap-2 font-semibold text-[15px] text-stone-950 dark:text-zinc-50">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logo-black.webp"
              alt=""
              width={20}
              height={20}
              className="dark:hidden"
              style={{ width: 20, height: 20, objectFit: 'contain' }}
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/logo-white.webp"
              alt=""
              width={20}
              height={20}
              className="hidden dark:block"
              style={{ width: 20, height: 20, objectFit: 'contain' }}
            />
            Lithosbase
          </div>
          <p className="text-[13px] leading-[1.5] text-stone-500 dark:text-zinc-400 mt-2.5 max-w-[36ch]">
            © {new Date().getFullYear()} Lithosbase. {t('rights')}
          </p>
        </div>
        <div className="flex gap-14 flex-wrap">
          {cols.map(({ label, links }) => (
            <div key={label} className="flex flex-col gap-2 min-w-[140px]">
              <div className="text-[11px] font-medium tracking-[0.1em] uppercase text-stone-950 dark:text-zinc-50 mb-1">
                {label}
              </div>
              {links.map(({ href, label: l }) => (
                <Link
                  key={l}
                  href={href}
                  className="text-[14px] text-stone-500 dark:text-zinc-400 hover:text-stone-950 dark:hover:text-zinc-50 transition-colors"
                >
                  {l}
                </Link>
              ))}
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
}

// ── Feature row ──────────────────────────────────────────────────────────────

export function FeatureRow({
  num,
  label,
  title,
  body,
  bullets,
  shot,
  flip = false,
  footer,
  id,
}: {
  num: string;
  label: string;
  title: string;
  body: string;
  bullets: string[];
  shot: ReactNode;
  flip?: boolean;
  footer?: ReactNode;
  id?: string;
}) {
  const copy = (
    <Reveal className="feature-copy">
      <div className="flex flex-col gap-5">
        <Eyebrow num={num} label={label} />
        <h2 className="text-[clamp(32px,4vw,44px)] font-bold tracking-tight leading-[1.1] m-0 max-w-[16ch]">
          {title}
        </h2>
        <p className="text-[17px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0 max-w-[48ch]">{body}</p>
        <ul className="list-none p-0 m-0 flex flex-col gap-2">
          {bullets.map(b => (
            <li key={b} className="flex items-start gap-2.5 text-[15px] leading-[1.5] text-stone-950 dark:text-zinc-50">
              <span
                className="mt-[9px] w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: 'var(--accent)' }}
              />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );

  const shotEl = (
    <Reveal delay={0.08} className="feature-shot">
      <div
        className="rounded-[16px] border border-stone-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden shadow-md"
        style={{ aspectRatio: '4/3' }}
      >
        {shot}
      </div>
    </Reveal>
  );

  return (
    <section id={id} className={footer ? 'pt-[clamp(64px,9vw,128px)]' : 'py-[clamp(64px,9vw,128px)]'} style={id ? { scrollMarginTop: '64px' } : undefined}>
      <style>{`
        @media (min-width: 1024px) {
          .feature-row-normal { grid-template-columns: 1fr 1.15fr; }
          .feature-row-flip   { grid-template-columns: 1.15fr 1fr; }
          .feature-row-flip .feature-shot { order: -1; }
        }
      `}</style>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
        <div className={`grid grid-cols-1 gap-8 lg:gap-20 items-center feature-row-${flip ? 'flip' : 'normal'}`}>
          {copy}
          {shotEl}
        </div>
      </div>
      {footer && <div className="mt-[clamp(48px,6vw,80px)]">{footer}</div>}
    </section>
  );
}
